/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { accessAPI, ArchivoPreview, Boton, TablaFiltrable } from "../../shared";

import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

// Archivos que los clientes enviaron a sus procesos desde el portal y que
// todavía nadie marcó como vistos. Mismo patrón que HallazgosPendientes.
const SEARCH_FIELDS = ["_clienteText", "_procesoText", "_archivoText", "nota", "usuario"];

function diasBody(a) {
  const d = a._dias;
  const severity = d <= 1 ? "success" : d <= 3 ? "warning" : "danger";
  return <Tag severity={severity} value={`${d} ${d === 1 ? "día" : "días"}`} />;
}

export default function ArchivosClientes({ toast }) {
  const [archivos, setArchivos] = useState();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/proceso/archivosclientes",
      null,
      (respuesta) => {
        setArchivos(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar los archivos enviados por clientes",
          sticky: true
        });
        setLoading(false);
      }
    );
  }, [toast]);

  if (!loading && (!archivos || archivos.length === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div css={{ marginTop: "1em" }}>
        <Skeleton width="100%" height="3.5rem" borderRadius="6px" />
      </div>
    );
  }

  function marcarComoVisto(a) {
    accessAPI(
      "PUT",
      `admin/proceso/${a.procesoId}/archivoscliente/${a.archivo.id}/visto`,
      null,
      () => {
        setArchivos((prev) =>
          prev.filter((x) => !(x.procesoId === a.procesoId && x.archivo.id === a.archivo.id))
        );
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al marcar como visto",
          sticky: true
        });
      }
    );
  }

  const datos = archivos.map((a) => ({
    ...a,
    _clienteText: a.cliente?.razonsocial || "-",
    _procesoText: `${a.procesoNombre} - ${a.procesoNombreFantasia}`,
    _archivoText: a.archivo?.nombredescarga || "-",
    _dias: Math.floor((Date.now() - new Date(a.fecha).getTime()) / 86400000)
  }));

  return (
    <Accordion
      activeIndex={null}
      css={{
        marginTop: "1em",
        "& .p-accordion-header-link": { color: "black" },
        "& .p-toggleable-content": { transition: "max-height 0.15s ease" }
      }}
    >
      <AccordionTab header={`Archivos enviados por clientes (${archivos.length})`}>
        <TablaFiltrable
          value={datos}
          searchFields={SEARCH_FIELDS}
          busquedaAlFinal
          anchoBusqueda="33%"
          nombreArchivo="archivos_enviados_por_clientes"
          size="small"
          emptyMessage="No hay archivos enviados por clientes"
          onRowClick={(e) => {
            if (e.originalEvent.target.closest("button, a")) return;
            // Lleva a la actividad a la que el cliente envió el archivo; los
            // anteriores, enviados al proceso, al proceso
            navigate(
              e.data.actividadId
                ? `/proceso/${e.data.procesoId}/actividades/${e.data.actividadId}`
                : `/proceso/${e.data.procesoId}`
            );
          }}
          rowClassName={() => "cursor-pointer"}
        >
          <Column field="_clienteText" header="Cliente" />
          <Column field="_procesoText" header="Proceso" />
          <Column
            header="Archivo"
            body={(a) => <ArchivoPreview nombre={a.archivo?.nombredescarga} hash={a.archivo?.hash} />}
          />
          <Column field="nota" header="Nota" body={(a) => a.nota || "-"} />
          <Column field="usuario" header="Enviado por" body={(a) => a.usuario || "-"} />
          <Column header="Días" field="_dias" sortable body={diasBody} style={{ width: "8em" }} />
          <Column
            body={(a) => (
              <Boton callback={() => marcarComoVisto(a)} chico texto>
                Marcar como visto
              </Boton>
            )}
            style={{ width: "12em" }}
          />
        </TablaFiltrable>
      </AccordionTab>
    </Accordion>
  );
}
